import React, { useCallback, useEffect, useState } from 'react'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Card, Collapse, Input, Layout, Modal, notification, PageHeader, Select, Space, Table, Typography } from 'antd'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { initializePages } from '~actions'
import { concessionFiltersEndpoint } from '~apiConfig'
import { getCurrentUser } from '~context'
import { Spinner } from '~stories'
import { getApiResponseInJSON, getIdToken } from '~utils'
import { toHyphenatedSlug } from '~utils/faqSlugFromVenueName'
import { resolveFlagColorFromInput } from '~utils/flagColor'

const { Panel } = Collapse
const PAGE_TITLE = 'Concession Filters'
const DEFAULT_PICKER_COLOR = '#ffffff'

const DEFAULT_LOCATION = {
  key: 'location',
  title: 'Location',
  sections: []
}

const DEFAULT_CUISINE = {
  key: 'cuisine',
  title: 'Cuisine',
  placeholder: 'All Cuisines',
  options: []
}

const DEFAULT_DIETARY = {
  key: 'dietary',
  title: 'Dietary Preferences',
  placeholder: 'No Dietary Preferences',
  options: []
}

const createRowId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

const keyToDisplayTitle = key =>
  String(key ?? '')
    .split('-')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

const resolveLocationTitle = item => {
  const title = String(item?.title ?? item?.label ?? '').trim()
  if (title) return title
  return keyToDisplayTitle(item?.key)
}

const stripFilterItem = ({ _rowId, ...item }) => ({
  ...item,
  key: item.key || toHyphenatedSlug(item.label)
})

const withRowIds = items =>
  (items || []).map(item => {
    const color =
      item.color && typeof item.color === 'object' && !Array.isArray(item.color)
        ? item.color
        : item.flags && typeof item.flags === 'object' && !Array.isArray(item.flags)
          ? item.flags
          : {}

    return {
      ...item,
      color,
      _rowId: item._rowId || createRowId()
    }
  })

const stripLocationItem = ({ _rowId, sections = [], title, label, key }) => ({
  title: resolveLocationTitle({ title, label, key }),
  key: key || toHyphenatedSlug(resolveLocationTitle({ title, label, key })),
  sections: (sections || []).map(stripLocationSubsection)
})

const stripLocationSubsection = ({ _rowId, ...item }) => ({
  ...item,
  label: String(item.label ?? '').trim(),
  key: item.key || toHyphenatedSlug(item.label),
  is_active: item.is_active !== false
})

const withLocationRowIds = items =>
  (items || []).map(item => {
    const title = resolveLocationTitle(item)
    return {
      ...item,
      title,
      key: item.key || toHyphenatedSlug(title),
      _rowId: item._rowId || createRowId(),
      sections: withSubsectionRowIds(item.sections)
    }
  })

const withSubsectionRowIds = items =>
  (items || []).map(item => ({
    ...item,
    _rowId: item._rowId || createRowId()
  }))

const validateLocationSections = sections => {
  for (let index = 0; index < sections.length; index += 1) {
    const location = sections[index]
    const title = resolveLocationTitle(location)
    if (!title) {
      return `Location row ${index + 1} requires a title.`
    }

    for (let subIndex = 0; subIndex < (location.sections || []).length; subIndex += 1) {
      const subsection = location.sections[subIndex]
      if (!String(subsection?.label ?? '').trim()) {
        return `Location row ${index + 1}, section ${subIndex + 1} requires a label.`
      }
    }
  }

  return null
}

const LocationSubsectionsTable = ({ items, onChange }) => {
  const updateLabel = (rowId, value) => {
    onChange(currentSections =>
      currentSections.map(item => {
        if (item._rowId !== rowId) return item
        return {
          ...item,
          label: value,
          key: toHyphenatedSlug(value)
        }
      })
    )
  }

  const addItem = () => {
    onChange(currentSections => [
      ...currentSections,
      {
        _rowId: createRowId(),
        key: '',
        label: '',
        is_active: true
      }
    ])
  }

  const removeItem = rowId => {
    onChange(currentSections => currentSections.filter(item => item._rowId !== rowId))
  }

  const columns = [
    {
      title: 'Section',
      dataIndex: 'label',
      render: (_, record) => (
        <Input
          onChange={e => updateLabel(record._rowId, e.target.value)}
          placeholder="Section label"
          value={record.label}
        />
      )
    },
    {
      title: '',
      width: 64,
      align: 'right',
      render: (_, record) => (
        <Button
          aria-label="Delete section row"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeItem(record._rowId)}
          type="text"
        />
      )
    }
  ]

  return (
    <>
      <Table
        columns={columns}
        dataSource={items}
        pagination={false}
        rowKey="_rowId"
        size="small"
      />
      <Button icon={<PlusOutlined />} onClick={addItem} style={{ marginTop: 12 }} type="dashed">
        Add Section
      </Button>
    </>
  )
}

LocationSubsectionsTable.propTypes = {
  items: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired
}

const LocationFilterEditor = ({ items, onChange }) => {
  const [activeLocationKey, setActiveLocationKey] = useState(null)

  const updateTitle = (rowId, value) => {
    onChange(prev =>
      prev.map(item => {
        if (item._rowId !== rowId) return item
        return {
          ...item,
          title: value,
          key: toHyphenatedSlug(value)
        }
      })
    )
  }

  const updateSections = (rowId, sectionsUpdater) => {
    onChange(prev =>
      prev.map(item => {
        if (item._rowId !== rowId) return item
        const currentSections = item.sections || []
        const nextSections =
          typeof sectionsUpdater === 'function'
            ? sectionsUpdater(currentSections)
            : sectionsUpdater
        return { ...item, sections: nextSections }
      })
    )
  }

  const addLocation = () => {
    const rowId = createRowId()
    onChange(prev => [
      ...prev,
      {
        _rowId: rowId,
        title: '',
        key: '',
        sections: []
      }
    ])
    setActiveLocationKey(rowId)
  }

  const removeLocation = rowId => {
    onChange(prev => prev.filter(item => item._rowId !== rowId))
    setActiveLocationKey(current => (current === rowId ? null : current))
  }

  const getLocationHeader = location =>
    resolveLocationTitle(location) || 'New location'

  return (
    <>
      <Collapse
        accordion
        activeKey={activeLocationKey}
        expandIconPosition="right"
        onChange={key => setActiveLocationKey(key || null)}
      >
        {items.map(location => (
          <Panel
            key={location._rowId}
            extra={(
              <Button
                aria-label="Delete location row"
                danger
                icon={<DeleteOutlined />}
                onClick={event => {
                  event.stopPropagation()
                  removeLocation(location._rowId)
                }}
                type="text"
              />
            )}
            header={getLocationHeader(location)}
          >
            <div style={{ maxWidth: 520 }}>
              <Typography.Text strong>Title</Typography.Text>
              <Input
                onChange={e => updateTitle(location._rowId, e.target.value)}
                placeholder="Display title"
                style={{ marginTop: 8 }}
                value={location.title || ''}
              />
            </div>
            <div style={{ marginTop: 16 }}>
              <LocationSubsectionsTable
                items={location.sections || []}
                onChange={sectionsUpdater => updateSections(location._rowId, sectionsUpdater)}
              />
            </div>
          </Panel>
        ))}
      </Collapse>
      <Button icon={<PlusOutlined />} onClick={addLocation} style={{ marginTop: 12 }} type="dashed">
        Add Location
      </Button>
    </>
  )
}

LocationFilterEditor.propTypes = {
  items: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired
}

const FilterOptionsTable = ({ items, onChange, sectionLabel }) => {
  const updateLabel = (rowId, value) => {
    onChange(items.map(item => {
      if (item._rowId !== rowId) return item
      return {
        ...item,
        label: value,
        key: toHyphenatedSlug(value)
      }
    }))
  }

  const updateColor = (rowId, value) => {
    onChange(items.map(item =>
      item._rowId === rowId
        ? { ...item, color: resolveFlagColorFromInput(value) || {} }
        : item
    ))
  }

  const getPickerColor = record => {
    const value = record.color?.value
    if (value && /^#[0-9a-fA-F]{6}$/.test(value)) {
      return value
    }
    return DEFAULT_PICKER_COLOR
  }

  const addItem = () => {
    onChange([
      ...items,
      {
        _rowId: createRowId(),
        key: '',
        label: '',
        color: {},
        is_active: true
      }
    ])
  }

  const removeItem = rowId => {
    onChange(items.filter(item => item._rowId !== rowId))
  }

  const confirmRemoveItem = record => {
    const itemName = record.label?.trim() || record.key?.trim() || 'this item'
    Modal.confirm({
      title: `Delete ${sectionLabel}?`,
      content: `Are you sure you want to delete "${itemName}"? This change will apply when you save.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => removeItem(record._rowId)
    })
  }

  const columns = [
    {
      title: 'Title',
      dataIndex: 'label',
      width: '35%',
      render: (_, record) => (
        <Input
          onChange={e => updateLabel(record._rowId, e.target.value)}
          placeholder="Display title"
          style={{ width: '100%' }}
          value={record.label}
        />
      )
    },
    {
      title: 'Color',
      width: '35%',
      render: (_, record) => (
        <Space size="small">
          <input
            aria-label={`${sectionLabel} color picker`}
            onChange={e => updateColor(record._rowId, e.target.value)}
            style={{
              width: 36,
              height: 32,
              padding: 2,
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              cursor: 'pointer',
              backgroundColor: '#fff',
              flexShrink: 0
            }}
            title={record.color?.value || 'Select color'}
            type="color"
            value={getPickerColor(record)}
          />
          <Input
            aria-label={`${sectionLabel} hex color`}
            onChange={e => updateColor(record._rowId, e.target.value)}
            placeholder="#RRGGBB"
            style={{ width: 120 }}
            value={record.color?.value || ''}
          />
        </Space>
      )
    },
    {
      title: '',
      width: 64,
      align: 'right',
      render: (_, record) => (
        <Button
          aria-label={`Delete ${sectionLabel} row`}
          danger
          icon={<DeleteOutlined />}
          onClick={() => confirmRemoveItem(record)}
          type="text"
        />
      )
    }
  ]

  return (
    <>
      <Table
        columns={columns}
        dataSource={items}
        pagination={false}
        rowKey="_rowId"
        size="small"
        style={{ width: '100%' }}
        tableLayout="fixed"
      />
      <Button icon={<PlusOutlined />} onClick={addItem} style={{ marginTop: 12 }} type="dashed">
        Add {sectionLabel}
      </Button>
    </>
  )
}

FilterOptionsTable.propTypes = {
  items: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  sectionLabel: PropTypes.string.isRequired
}

const ConcessionFiltersAdmin = ({ initializePages, venues }) => {
  const { access_token: accessToken, userDetails: { isContentEditor } } = getCurrentUser()
  const [selectedVenueId, setSelectedVenueId] = useState('')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [locationSections, setLocationSections] = useState([])
  const [cuisineOptions, setCuisineOptions] = useState([])
  const [dietaryOptions, setDietaryOptions] = useState([])

  useEffect(() => {
    initializePages(accessToken)
  }, [accessToken, initializePages])

  const loadFilters = useCallback(async venueId => {
    if (!venueId) return

    setLoading(true)
    try {
      const { method, url } = concessionFiltersEndpoint.get(venueId)
      const idToken = getIdToken()
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${idToken}` },
        method
      }).then(async res => await getApiResponseInJSON(res))

      const filters = response?.filters || {}
      const locationData = filters.locations || filters.location || DEFAULT_LOCATION
      setLocationSections(withLocationRowIds(locationData.sections))
      setCuisineOptions(withRowIds(filters.cuisine?.options))
      setDietaryOptions(withRowIds(filters.dietary?.options))
    } catch (error) {
      notification.error({ message: error.message || 'Failed to load concession filters' })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (selectedVenueId) {
      loadFilters(selectedVenueId)
    }
  }, [selectedVenueId, loadFilters])

  const handleVenueChange = venueId => {
    setSelectedVenueId(venueId || '')
    if (!venueId) {
      setLocationSections([])
      setCuisineOptions([])
      setDietaryOptions([])
    }
  }

  const handleSave = async () => {
    if (!isContentEditor) {
      notification.error({ message: 'You are not authorized to perform this action.' })
      return
    }
    if (!selectedVenueId) {
      notification.error({ message: 'Please select a venue.' })
      return
    }

    const locationValidationError = validateLocationSections(locationSections)
    if (locationValidationError) {
      notification.error({ message: locationValidationError })
      return
    }

    const selectedVenue = venues?.find(v => String(v._id) === String(selectedVenueId))
    const payload = {
      venue: {
        name: selectedVenue?.name || '',
        slug: selectedVenue?.slug || ''
      },
      locations: {
        ...DEFAULT_LOCATION,
        venue_id: selectedVenueId,
        sections: locationSections.map(stripLocationItem)
      },
      cuisine: {
        ...DEFAULT_CUISINE,
        options: cuisineOptions.map(stripFilterItem)
      },
      dietary: {
        ...DEFAULT_DIETARY,
        options: dietaryOptions.map(stripFilterItem)
      }
    }

    setSaving(true)
    try {
      const { method, url } = concessionFiltersEndpoint.update(selectedVenueId)
      const idToken = getIdToken()
      const response = await fetch(url, {
        body: JSON.stringify(payload),
        headers: {
          Authorization: `Bearer ${idToken}`,
          AccessToken: accessToken,
          'Content-Type': 'application/json'
        },
        method
      }).then(async res => await getApiResponseInJSON(res))

      if (response?.success === false) {
        throw new Error(response?.message || 'Failed to save concession filters')
      }

      notification.success({ message: response?.message || 'Concession filters saved' })
      await loadFilters(selectedVenueId)
    } catch (error) {
      notification.error({ message: error.message || 'Failed to save concession filters' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Layout>
      <PageHeader
        extra={
          <Button loading={saving} onClick={handleSave} type="primary">
            Save
          </Button>
        }
        title={PAGE_TITLE}
      />
      <Layout.Content style={{ margin: 24 }}>
        <Card>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Typography.Text strong>Venue</Typography.Text>
              <Select
                allowClear
                onChange={handleVenueChange}
                optionFilterProp="children"
                placeholder="Select a venue"
                showSearch
                style={{ width: '100%', marginTop: 8, maxWidth: 480 }}
                value={selectedVenueId || undefined}
              >
                {(venues || []).map(venue => (
                  <Select.Option key={venue._id} value={venue._id}>
                    {venue.name}
                  </Select.Option>
                ))}
              </Select>
            </div>

            {loading ? (
              <Spinner isLoading size="large" />
            ) : selectedVenueId ? (
              <Collapse defaultActiveKey={['location', 'cuisine', 'dietary']}>
                <Panel key="location" header="LOCATION">
                  <LocationFilterEditor
                    items={locationSections}
                    onChange={setLocationSections}
                  />
                </Panel>
                <Panel key="cuisine" header="CUISINE">
                  <FilterOptionsTable
                    items={cuisineOptions}
                    onChange={setCuisineOptions}
                    sectionLabel="Cuisine"
                  />
                </Panel>
                <Panel key="dietary" header="DIETARY PREFERENCES">
                  <FilterOptionsTable
                    items={dietaryOptions}
                    onChange={setDietaryOptions}
                    sectionLabel="Dietary Preference"
                  />
                </Panel>
              </Collapse>
            ) : (
              <Typography.Text type="secondary">
                Select a venue to manage concession filter options.
              </Typography.Text>
            )}
          </Space>
        </Card>
      </Layout.Content>
    </Layout>
  )
}

const mapStateToProps = ({ pages }) => ({
  venues: pages.venues
})

const mapDispatchToProps = {
  initializePages
}

ConcessionFiltersAdmin.propTypes = {
  initializePages: PropTypes.func.isRequired,
  venues: PropTypes.array
}

export default connect(mapStateToProps, mapDispatchToProps)(ConcessionFiltersAdmin)
